import React from "react";
import Head from "next/head";
import { Box, Heading, VStack, Text, Button, Flex, useColorMode } from "@chakra-ui/react";
import { useRouter } from "next/router";
import { motion } from "framer-motion";

const MotionBox = motion(Box);

const CaseStudiesPage = () => {
  const { colorMode } = useColorMode();
  const bgColor = colorMode === "dark" ? "gray.900" : "gray.50";
  const textColor = colorMode === "dark" ? "white" : "gray.700";
  const router = useRouter();

  const caseStudies = [
    {
      title: "Setting a Global Capability Centre and scaling it",
      description:
        "Discover how AI-powered recruitment enabled a multinational to build a Center of Excellence with 200 niche roles in one year, achieving 33% cost savings and 70% time reduction.",
      link: "/caseStudy6",
      publishDate: "2024-03-15",
    },
    {
      title: "Transforming a Training Institute with Aptofy's AI Solutions",
      description:
        "Discover how Aptofy revolutionized training institutes with AI-powered solutions, ensuring efficiency and scalability.",
      link: "/caseStudy1",
      publishDate: "2024-01-15",
    },
    {
      title: "Building High Performance Teams the Smart and Sustainable Way",
      description:
        "Learn how Aptofy's bias-free solutions enabled sustainable and high-performance hiring on a global scale.",
      link: "/caseStudy2",
      publishDate: "2024-02-01",
    },
    {
      title: "Transforming Global Hiring with Aptofy's AI-Powered Solutions",
      description:
        "Explore how Aptofy's AI-powered platform helped businesses transform their global hiring processes.",
      link: "/caseStudy3",
      publishDate: "2024-02-15",
    },
    {
      title: "Transforming Recruitment for a Scaling Fintech in the BFSI Industry",
      description:
        "Learn how Aptofy's AI-powered recruitment solutions can help fintech companies streamline their hiring process while focusing on quality and diversity.",
      link: "/caseStudy4",
      publishDate: "2024-03-01",
    },
    {
      title: "Empowering Semiconductor & VLSI Startups with Strategic Talent Solutions",
      description: 
        "Discover how Aptofy is revolutionizing talent acquisition for semiconductor startups through AI-powered assessments, university partnerships, and specialized recruitment strategies.",
      link: "/caseStudy5",
      publishDate: "2024-01-07",
    }
    
  ];

  // Generate structured data for SEO
  const generateStructuredData = () => ({
    "@context": "https://schema.org",
    "@type": "ItemList",
    "itemListElement": caseStudies.map((study, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "item": {
        "@type": "Article",
        "headline": study.title,
        "description": study.description,
        "datePublished": study.publishDate,
        "url": `https://www.aptofy.com${study.link}`,
        "author": {
          "@type": "Organization",
          "name": "Aptofy"
        },
        "publisher": {
          "@type": "Organization",
          "name": "Aptofy",
          "logo": {
            "@type": "ImageObject",
            "url": "https://www.aptofy.com/images/aptofy-logo.png"
          }
        }
      },
    })),
  });

  return (
    <>
      <Head>
        <title>Case Studies | Aptofy - AI-Powered Business Solutions & GCC Scaling</title>
        <meta
          name="description"
          content="Explore real-world success stories of how Aptofy's AI solutions transformed businesses, scaled GCCs, revolutionized training institutes, and optimized global hiring processes across industries."
        />
        <meta
          name="keywords"
          content="AI case studies, GCC scaling India, business transformation, AI-powered solutions, training institute solutions, recruitment solutions, hiring solutions, Aptofy, case study, Global Capability Center, Center of Excellence, niche talent acquisition, AI recruitment, bias-free hiring, sustainable recruitment, fintech hiring, semiconductor talent, VLSI recruitment"
        />
        <meta property="og:title" content="Case Studies | Aptofy - AI-Powered Business Solutions & GCC Scaling" />
        <meta
          property="og:description"
          content="Discover how Aptofy's AI solutions helped scale GCCs, transform global hiring, and revolutionize business processes with real-world success stories."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.aptofy.com/caseStudy" />
        <meta property="og:image" content="https://www.aptofy.com/images/case-studies-banner.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Case Studies | Aptofy - AI-Powered Business Solutions" />
        <meta name="twitter:description" content="Real-world success stories of AI-powered business transformation" />
        <meta name="twitter:image" content="https://www.aptofy.com/images/case-studies-banner.jpg" />
        <link rel="canonical" href="https://www.aptofy.com/caseStudy" />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(generateStructuredData()) }}
        />
      </Head>
      <Box p={8} bg={colorMode === "dark" ? "gray.900" : "gray.50"} minH="100vh">
        <Heading size="xl" mb={8} textAlign="center" color={colorMode === "dark" ? "white" : "gray.800"}>
          Case Studies
        </Heading>
        <Text 
          textAlign="center" 
          mb={12} 
          fontSize="lg" 
          color={textColor}
          maxW="800px" 
          mx="auto"
        >
          Discover how Aptofy's AI-powered solutions transform the recruitment process 
          and empower organizations to achieve their goals.
        </Text>
        <Flex
          direction={["column", "column", "row"]}
          justify="center"
          align="center"
          gap={8}
          flexWrap="wrap"
        >
          {caseStudies.map((study, index) => (
            <MotionBox
              key={index}
              bg={colorMode === "dark" ? "gray.800" : "white"}
              p={6}
              rounded="lg"
              shadow="md"
              w={["100%", "80%", "45%"]}
              _hover={{ shadow: "lg" }}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                duration: 0.5,
                delay: index * 0.1,
                ease: "easeInOut",
              }}
              border={colorMode === "dark" ? "1px solid" : "none"}
              borderColor={colorMode === "dark" ? "gray.700" : "transparent"}
            >
              <VStack align="start" spacing={4}>
                <Heading size="md" color="blue.500" lineHeight="1.3">
                  {study.title}
                </Heading>
                <Text color={textColor} fontSize="sm" lineHeight="1.5">
                  {study.description}
                </Text>
                <Button
                  colorScheme="blue"
                  onClick={() => router.push(study.link)}
                  size="sm"
                  alignSelf="start"
                >
                  Read More
                </Button>
              </VStack>
            </MotionBox>
          ))}
        </Flex>
      </Box>
    </>
  );
};

export default CaseStudiesPage;